import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import type { PostgrestSingleResponse } from '@supabase/supabase-js';
import { User, AuthState, UserRole } from '../types/user';
import type { Profile } from '../utils/profileManager';
import { supabase, isSupabaseConfigured, clearSupabaseAuthStorage } from '../lib/supabase';
import {
  verifyAuthSessionPersisted,
  hasPersistedAuthSession,
  parsePersistedSupabaseSession,
} from '../lib/authStorage';
import {
  getCachedValue,
  runSupabaseQuery,
  withTimeout,
  isSupabaseTimeoutError,
  invalidateCachedValuesByPrefix,
} from '../utils/supabaseSafeQuery';
import { getSupabaseFreeTierMessage } from '../utils/supabaseFreeTierErrors';
import { authenticatePasskey } from '../utils/passkey';
import {
  clearRoleViewOverride,
  getRoleViewOverride,
  resolveEffectiveRole,
  restorePlatformOwnerSuperAdminDashboard,
} from '../utils/roleViewOverride';
import {
  clearSaasMembershipCache,
  ensureTbSiteWorkspaceOrg,
  ensureWorkspaceOrg,
  ensureSaasOwnerProfile,
  fetchMyOrg,
  setWorkspaceOrg,
} from '../saas/lib/saasOrg';
import {
  attachSignedInCustomerToOrganization,
  CUSTOMER_SAME_ORG_ACCOUNT_MESSAGE,
  lookupCustomerAccountScope,
  orgScopedCustomerAuthEmail,
  resolveCustomerLoginIdentity,
} from '../saas/lib/customerOrgAccount';
import { ensureImpersonationWorkspacePin } from '../saas/lib/platformImpersonation';
import { fetchUserPrimaryOrgId, restoreActorWorkspaceOrg } from '../saas/lib/impersonationWorkspace';
import { setActiveWorkspaceOrgId } from '../saas/lib/activeWorkspaceOrgId';
import { getAppMode } from '../saas/appMode';
import {
  isPlatformSuperAdminEmail,
  resolveBootstrapDashboardRole,
  resolvePlatformOwnerDashboardRole,
} from '../utils/platformSuperAdminEmail';
import {
  buildOAuthRedirectUrl,
  stripOAuthParamsFromUrl,
  ensureTbCanonicalHost,
} from '../utils/tbOAuthRedirect';
import { stashSaasOAuthReturn } from '../saas/lib/saasOAuthReturn';
import {
  fetchSaasGoogleOAuthClientId,
  shouldUseServiceAppGoogleAuth,
  signInWithServiceAppGoogle,
} from '../saas/lib/saasGoogleAuth';
import { getRuntimeAiSupportContact } from '../utils/aiChatBranding';
import { SAAS_ORG_SLUG_KEY } from '../saas/lib/embedUrls';
import { buildEmailOptionalPhoneEqOrFilter } from '../saas/lib/postgrestFilterSafety';
import { isPlaceholderCustomerEmail } from '../utils/customerEmail';
import { buildProfileGuestEmail } from '../utils/profileManager';
import { resolveAuthSessionEmailFromProfile } from '../saas/lib/authSessionEmail';
import { mapMagicLinkSessionError, readMagicLinkSessionProfile } from '../saas/lib/magicLinkSessionErrors';
import {
  clearImpersonationOverride,
  getImpersonationOverride,
  impersonationTargetToUser,
  resolveEffectiveIdentity,
  setImpersonationOverride,
  type ImpersonationTarget,
} from '../utils/userImpersonationOverride';
import {
  logSecurityEvent,
  recordAuthLoginOutcome,
  profileAuditOrganizationId,
} from '../utils/securityLogger';
import {
  clearOAuthPending,
  consumeOAuthErrorFromUrl,
  consumeOAuthLoginAudit,
  isOAuthPending,
  markOAuthCallbackError,
  markOAuthPending,
  stashOAuthLoginAudit,
} from '../utils/authAuditFlags';

type RegisterUserRole = User['role'] | 'customer';

interface RegisterUserData {
  email?: string | null;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  role?: RegisterUserRole;
  hasRealEmail?: boolean;
  address?: string | null;
  city?: string | null;
  /**
   * Tenant to file the customer under. Needed on a tenant custom domain, where
   * the org comes from the hostname and there is no `?org=` slug to look up.
   */
  organizationId?: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithMagicLink: (token: string) => Promise<{
    link: { purpose: string; metadata: Record<string, unknown> | null };
    profile: Profile;
  }>;
  loginWithOTP: (profile: any, authToken?: string) => Promise<void>;
  loginWithPasskey: (cachedOptionsJSON?: Record<string, unknown>) => Promise<void>;
  loginWithGoogle: () => Promise<'redirect' | 'session'>;
  loginWithEnterpriseSSO: (params: { domain?: string; providerId?: string }) => Promise<void>;
  logout: () => void;
  register: (userData: RegisterUserData) => Promise<void>;
  mustChangePassword: boolean;
  refreshUser: () => Promise<void>;
  /** Real signed-in profile (never swapped during impersonation). */
  realUser: User | null;
  isImpersonating: boolean;
  /** View app as another user (super admin only). */
  applyImpersonation: (
    target: ImpersonationTarget,
    options?: {
      workspaceOrgId?: string;
      actorIsPlatformAdmin?: boolean;
      actorIsOrgOwner?: boolean;
      viewAsRole?: string;
      rolePreview?: boolean;
    },
  ) => Promise<void>;
  clearImpersonation: () => Promise<void>;
  /** Instant role update after DB write — avoids full page reload on super-admin role switch. */
  applyRoleSwitch: (newRole: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const PASSWORD_RECOVERY_FLAG = 'tb:auth:password-recovery';

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Safe for workspace boot so a remount never whitescreens the app. */
export function useOptionalAuth() {
  return useContext(AuthContext) ?? null;
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Email/password signups create a profiles row in-app; Google OAuth does not.
 * Inserts a customer profile using Google user_metadata when the user has a Google identity.
 */
async function ensureGoogleProfileFromSession(userId: string): Promise<boolean> {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error || !user || user.id !== userId) {
    return false;
  }
  const hasGoogle = user.identities?.some((i) => i.provider === 'google');
  if (!hasGoogle) {
    return false;
  }
  const email = user.email?.trim();
  if (!email) {
    return false;
  }
  const meta = user.user_metadata || {};
  let firstName = String(meta.given_name ?? '').trim();
  let lastName = String(meta.family_name ?? '').trim();
  if (!firstName && !lastName) {
    const full = String(meta.full_name ?? meta.name ?? '').trim();
    const parts = full.split(/\s+/).filter(Boolean);
    firstName = parts[0] || 'User';
    lastName = parts.slice(1).join(' ');
  }
  if (!firstName) {
    firstName = 'User';
  }

  if (getAppMode() === 'saas-platform') {
    const { data: membership } = await supabase
      .from('organization_members')
      .select('id')
      .eq('user_id', userId)
      .eq('status', 'active')
      .limit(1)
      .maybeSingle();
    if (membership) {
      return true;
    }
  }

  const profileRole = isPlatformSuperAdminEmail(email) ? resolvePlatformOwnerDashboardRole() : 'customer';

  const { error: insertError } = await supabase.from('profiles').insert({
    id: userId,
    email,
    first_name: firstName,
    last_name: lastName,
    phone: null,
    role: profileRole,
    has_real_email: true,
    is_active: true,
  });

  if (insertError) {
    if (insertError.code === '23505' || (insertError as { status?: number }).status === 409) {
      return true;
    }
    console.error('ensureGoogleProfileFromSession:', insertError);
    return false;
  }
  return true;
}

function isMobileBrowser(): boolean {
  return typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function authSessionCheckTimeoutMs(): number {
  return isMobileBrowser() ? 15000 : 8000;
}

/**
 * Longest the loading screen may wait for workspace-org (RLS) sync before rendering anyway.
 * Kept short: dashboards await ensureTbSiteWorkspaceOrg() themselves (the call is de-duplicated)
 * before querying, so this only smooths the common fast case instead of blanking the page.
 */
const WORKSPACE_SYNC_GATE_MS = 1200;

/** Stale-token recoveries allowed per page load before we treat the session as dead. */
const MAX_STALE_SESSION_RECOVERIES = 2;

function buildSessionBootstrapUser(sessionUser: { id: string; email?: string | null }): User {
  const cacheKey = `profile:${sessionUser.id}`;
  const cached = getCachedValue<PostgrestSingleResponse<any>>(cacheKey);
  const profile = cached?.data;
  const email = (sessionUser.email || profile?.email || '').trim();
  const role = resolveBootstrapDashboardRole(email, profile?.role, sessionUser.id);

  return {
    id: sessionUser.id,
    email,
    firstName: profile?.first_name ?? '',
    lastName: profile?.last_name ?? '',
    phone: profile?.phone ?? '',
    role,
    createdAt: profile?.created_at ?? new Date().toISOString(),
    isActive: profile?.is_active !== false,
  };
}

/** Keep the dashboard mounted across refresh while the JWT session is verified. */
function getInitialAuthState(): AuthState {
  if (typeof window === 'undefined') {
    return { user: null, isAuthenticated: false, isLoading: true };
  }
  try {
    // During Google/SSO return (or while redirect is pending), never hydrate the previous
    // email/password session — that made OAuth look broken ("still logged in as password user").
    const hasOAuthCode = new URLSearchParams(window.location.search).has('code');
    if (hasOAuthCode || isOAuthPending()) {
      return { user: null, isAuthenticated: false, isLoading: true };
    }

    const supabaseUrl =
      typeof import.meta !== 'undefined' && import.meta.env
        ? (import.meta.env.VITE_SUPABASE_URL as string | undefined)
        : undefined;
    const parsed = parsePersistedSupabaseSession(supabaseUrl);
    if (parsed?.hasRefreshToken) {
      return {
        user: buildSessionBootstrapUser({ id: parsed.userId, email: parsed.email }),
        isAuthenticated: true,
        isLoading: true,
      };
    }
  } catch {
    /* fall through */
  }
  return { user: null, isAuthenticated: false, isLoading: true };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [realUser, setRealUser] = useState<User | null>(() => getInitialAuthState().user);

  const resolveWorkspaceAuditOrgId = async (): Promise<string | undefined> => {
    try {
      const workspace = await ensureWorkspaceOrg();
      return workspace?.id;
    } catch {
      return undefined;
    }
  };
  const [isImpersonating, setIsImpersonating] = useState(false);
  const isInitializedRef = useRef(false);
  const loginInProgressRef = useRef(false);
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  // The workspace-sync gate may only hold the spinner once, before first paint.
  // Re-arming it later blanks a working dashboard (periodic session checks) or hangs
  // the app forever when a dead refresh token keeps emitting SIGNED_OUT.
  const readyGateSettledRef = useRef(false);
  const staleSessionRecoveryRef = useRef(0);

  const markInitialized = useCallback(() => {
    isInitializedRef.current = true;
  }, []);

  const markPasswordRecovery = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(PASSWORD_RECOVERY_FLAG, 'true');
    } catch (error) {
      console.warn('Unable to persist password recovery state:', error);
    }
  }, []);

  const clearPasswordRecovery = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.removeItem(PASSWORD_RECOVERY_FLAG);
    } catch (error) {
      console.warn('Unable to clear password recovery state:', error);
    }
  }, []);

  const getRecoveryFragmentForRedirect = useCallback(() => {
    if (typeof window === 'undefined') {
      return '';
    }

    const { hash, search } = window.location;
    if (hash && hash.toLowerCase().includes('type=recovery')) {
      return hash;
    }

    if (search && search.toLowerCase().includes('type=recovery')) {
      return `#${search.substring(1)}`;
    }

    return '';
  }, []);

  const isPasswordRecoveryFlowActive = useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const recoveryFragment = getRecoveryFragmentForRedirect();
    if (recoveryFragment) {
      markPasswordRecovery();
      return true;
    }

    try {
      return sessionStorage.getItem(PASSWORD_RECOVERY_FLAG) === 'true';
    } catch (error) {
      console.warn('Unable to read password recovery state:', error);
      return false;
    }
  }, [getRecoveryFragmentForRedirect, markPasswordRecovery]);

  const fetchUserProfile = useCallback(async (userId: string): Promise<User | null> => {
    const cacheKey = `profile:${userId}`;

    try {
      const response = await runSupabaseQuery<PostgrestSingleResponse<any>>(
        (signal) => {
          let query = supabase.from('profiles').select('*').eq('id', userId).limit(1);

          if (typeof (query as any).abortSignal === 'function') {
            query = (query as any).abortSignal(signal);
          }

          return query.maybeSingle();
        },
        {
          cacheKey,
          cacheTtlMs: 5 * 60 * 1000,
          timeoutMs: 12000,
        },
      );

      if (response.error) {
        throw response.error;
      }

      let data = response.data;
      if (!data) {
        const ensured = await ensureGoogleProfileFromSession(userId);
        if (ensured) {
          const retry = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
          if (!retry.error && retry.data) {
            data = retry.data;
          }
        }
      }

      if (data) {
        if (data.is_active === false) {
          try {
            sessionStorage.setItem('tb:auth:deactivated', '1');
          } catch {
            /* ignore */
          }
          await supabase.auth.signOut();
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return null;
        }
        let profileRole = data.role as User['role'];
        if (isPlatformSuperAdminEmail(data.email)) {
          profileRole = resolvePlatformOwnerDashboardRole();
          invalidateCachedValuesByPrefix('profile:');
        }
        const realUserProfile: User = {
          id: data.id,
          email: data.email,
          firstName: data.first_name,
          lastName: data.last_name,
          phone: data.phone,
          role: profileRole,
          createdAt: data.created_at,
          isActive: data.is_active !== false,
        };

        setRealUser(realUserProfile);
        const { user: effectiveUser, isImpersonating: impersonating } =
          resolveEffectiveIdentity(realUserProfile);
        setIsImpersonating(impersonating);

        setMustChangePassword(data.must_change_password || false);

        setAuthState({
          user: effectiveUser,
          isAuthenticated: true,
          isLoading: false,
        });
        return effectiveUser;
      }

      setAuthState((prev) => ({ ...prev, isLoading: false }));
      return null;
    } catch (error) {
      if (isSupabaseTimeoutError(error)) {
        console.warn('Profile fetch timed out, using cached profile if available.');
      } else {
        console.error('Error fetching profile:', error);
      }

      const cached = getCachedValue<PostgrestSingleResponse<any>>(cacheKey);
      if (cached?.data) {
        const data = cached.data;
        let profileRole = data.role as User['role'];
        if (isPlatformSuperAdminEmail(data.email)) {
          profileRole = resolvePlatformOwnerDashboardRole();
        }
        const realUserProfile: User = {
          id: data.id,
          email: data.email,
          firstName: data.first_name,
          lastName: data.last_name,
          phone: data.phone,
          role: profileRole,
          createdAt: data.created_at,
          isActive: data.is_active !== false,
        };

        setRealUser(realUserProfile);
        const { user: effectiveUser, isImpersonating: impersonating } =
          resolveEffectiveIdentity(realUserProfile);
        setIsImpersonating(impersonating);

        setMustChangePassword(data.must_change_password || false);
        setAuthState({
          user: effectiveUser,
          isAuthenticated: true,
          isLoading: false,
        });
        return effectiveUser;
      } else {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
        return null;
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let sessionCheckInterval: NodeJS.Timeout;
    let isCheckingSession = false;
    let loadingTimeout: NodeJS.Timeout;

    // Safety timeout: Force loading to false after 15s so slow mobile networks still show login
    loadingTimeout = setTimeout(() => {
      if (!mounted) return;
      // Use functional update — closure `authState.isLoading` is stale across long waits.
      setAuthState((prev) => {
        if (!prev.isLoading) return prev;
        console.warn('Auth loading timeout reached, forcing loading to false');
        return { ...prev, isLoading: false };
      });
      markInitialized();
    }, 15000);

    const clearAuthStorage = () => {
      try {
        clearSupabaseAuthStorage(import.meta.env.VITE_SUPABASE_URL);
      } catch (error) {
        console.error('Error clearing auth storage:', error);
      }
    };

    const sessionTimeoutMs = authSessionCheckTimeoutMs();

    const bootstrapSessionUser = (
      sessionUser: { id: string; email?: string | null },
      expiresAt?: number | null,
      options?: { deferReady?: boolean },
    ) => {
      const fallbackUser = buildSessionBootstrapUser(sessionUser);
      // Keep isLoading true until workspace org sync finishes — otherwise RLS returns empty
      // dashboards on refresh ("data not loading") before request_org_id is set.
      const deferReady = options?.deferReady !== false && !readyGateSettledRef.current;
      if (mounted) {
        setAuthState((prev) => {
          const email = (sessionUser.email || prev.user?.email || fallbackUser.email || '').trim();
          const resolvedRole = resolveEffectiveRole(sessionUser.id, fallbackUser.role, email) as User['role'];

          // Never downgrade a staff dashboard that is already loaded (late session check vs login).
          const keepRole =
            prev.user?.id === fallbackUser.id &&
            prev.isAuthenticated &&
            prev.user.role !== 'customer' &&
            resolvedRole === 'customer';
          const role = keepRole ? prev.user!.role : resolvedRole;
          return {
            user: { ...fallbackUser, role },
            isAuthenticated: true,
            isLoading: deferReady ? true : false,
          };
        });
        markInitialized();
      }
      void (async () => {
        const settleReady = () => {
          if (!deferReady) return;
          readyGateSettledRef.current = true;
          if (mounted) {
            setAuthState((prev) => (prev.isLoading ? { ...prev, isLoading: false } : prev));
          }
        };
        // Hard deadline: a stalled workspace/profile query must never freeze the app on
        // the loading screen. Worst case the dashboard mounts and refetches once org syncs.
        const gateDeadline = setTimeout(settleReady, WORKSPACE_SYNC_GATE_MS);
        try {
          const saasBootstrap =
            getAppMode() === 'saas-platform'
              ? (async () => {
                  await ensureImpersonationWorkspacePin(sessionUser.id);
                  await Promise.all([ensureWorkspaceOrg(), ensureSaasOwnerProfile()]);
                })()
              : Promise.resolve();
          await Promise.all([ensureTbSiteWorkspaceOrg(), saasBootstrap]);
          if (mounted) await fetchUserProfile(sessionUser.id);
        } catch {
          /* fetchUserProfile sets auth state on failure */
        } finally {
          clearTimeout(gateDeadline);
          settleReady();
        }
      })();
      if (expiresAt) {
        const expiryTime = expiresAt * 1000;
        const timeUntilExpiry = expiryTime - Date.now();
        if (timeUntilExpiry < 15 * 60 * 1000 && timeUntilExpiry > 0) {
          supabase.auth.refreshSession().catch(() => {});
        }
      }
    };

    const tryRefreshPersistedSession = async (maxAttempts = isMobileBrowser() ? 3 : 2): Promise<boolean> => {
      if (!hasPersistedAuthSession(import.meta.env.VITE_SUPABASE_URL)) return false;

      // Single-flight: concurrent refreshSession calls rotate the same refresh token and
      // invalidate each other — that was signing users out on page refresh.
      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }

      const run = (async (): Promise<boolean> => {
        let definitivelyInvalid = false;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            const { data: refreshed, error: refreshError } = await withTimeout(
              () => supabase.auth.refreshSession(),
              sessionTimeoutMs,
              'Supabase session refresh timed out',
            );
            if (refreshed?.session?.user && !refreshError) {
              bootstrapSessionUser(refreshed.session.user, refreshed.session.expires_at);
              return true;
            }
            const refreshMsg = (refreshError?.message || '').toLowerCase();
            const definitive =
              refreshMsg.includes('refresh_token_not_found') ||
              refreshMsg.includes('invalid refresh token') ||
              refreshMsg.includes('refresh token not found') ||
              refreshMsg.includes('already used') ||
              refreshMsg.includes('invalid_grant');
            if (definitive) {
              definitivelyInvalid = true;
              break;
            }
          } catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            const isTransient =
              isSupabaseTimeoutError(error) ||
              msg.toLowerCase().includes('timeout') ||
              msg.toLowerCase().includes('network') ||
              msg.toLowerCase().includes('fetch');
            if (isTransient && attempt < maxAttempts) {
              await new Promise((r) => setTimeout(r, 800 * attempt));
              continue;
            }
          }
        }

        // The server rejected the token outright: retrying it only loops through
        // SIGNED_OUT -> recover -> refresh -> SIGNED_OUT and pins the app on the spinner.
        if (definitivelyInvalid) {
          clearAuthStorage();
          supabase.auth.signOut().catch(() => {});
          return false;
        }

        // Offline / slow network: keep user signed in from stored session until refresh succeeds.
        const parsed = parsePersistedSupabaseSession(import.meta.env.VITE_SUPABASE_URL);
        if (parsed?.hasRefreshToken) {
          if (staleSessionRecoveryRef.current >= MAX_STALE_SESSION_RECOVERIES) {
            return false;
          }
          staleSessionRecoveryRef.current += 1;
          bootstrapSessionUser({ id: parsed.userId, email: parsed.email }, null, {
            deferReady: false,
          });
          // Background retry — do not await (avoids overlapping with the just-finished attempt).
          void supabase.auth.refreshSession().catch(() => {});
          return true;
        }

        return false;
      })();

      refreshInFlightRef.current = run;
      try {
        return await run;
      } finally {
        if (refreshInFlightRef.current === run) {
          refreshInFlightRef.current = null;
        }
      }
    };

    const checkSession = async () => {
      if (!mounted || isCheckingSession || loginInProgressRef.current) {
        return;
      }

      isCheckingSession = true;

      try {
        // Supabase/Google returned ?error=... (e.g. Unable to exchange external code).
        // Stop the loading gate and stash a message for the Sign In modal.
        const urlOauthError = consumeOAuthErrorFromUrl();
        if (urlOauthError) {
          clearOAuthPending();
          markOAuthCallbackError(urlOauthError);
          if (mounted) {
            setAuthState({ user: null, isAuthenticated: false, isLoading: false });
            markInitialized();
          }
          return;
        }

        const oauthCode =
          typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('code') : null;
        const hadOAuthCode = Boolean(oauthCode);

        // Explicit PKCE exchange on Google/SSO return. Relying only on getSession() +
        // detectSessionInUrl raced the marketing homepage paint and often left users
        // on `/` logged out after Google redirected back.
        let session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'] = null;
        let sessionError: Awaited<ReturnType<typeof supabase.auth.getSession>>['error'] = null;

        if (oauthCode) {
          const exchanged = await withTimeout(
            () => supabase.auth.exchangeCodeForSession(oauthCode),
            sessionTimeoutMs,
            'Supabase OAuth code exchange timed out',
          );
          if (exchanged.data.session?.user) {
            session = exchanged.data.session;
            sessionError = null;
          } else {
            // detectSessionInUrl may already have consumed the code during client init.
            const fallback = await withTimeout(
              () => supabase.auth.getSession(),
              sessionTimeoutMs,
              'Supabase session check timed out',
            );
            session = fallback.data.session;
            const exchangeMsg = (exchanged.error?.message || '').toLowerCase();
            const flowStateLost =
              exchangeMsg.includes('flow_state') ||
              exchangeMsg.includes('code verifier') ||
              exchangeMsg.includes('pkce');
            if (!session?.user && flowStateLost) {
              clearOAuthPending();
              clearAuthStorage();
              markOAuthCallbackError(
                'Google sign-in expired before it finished. Please click Continue with Google again and complete it in the same browser tab (do not reopen an old ?code= link).',
              );
              stripOAuthParamsFromUrl();
              if (mounted) {
                setAuthState({ user: null, isAuthenticated: false, isLoading: false });
                markInitialized();
              }
              return;
            }
            sessionError = session?.user ? null : fallback.error || exchanged.error;
          }
        } else {
          const result = await withTimeout(
            () => supabase.auth.getSession(),
            sessionTimeoutMs,
            'Supabase session check timed out',
          );
          session = result.data.session;
          sessionError = result.error;
        }

        if (sessionError) {
          const msg = sessionError?.message ?? '';
          const msgLower = msg.toLowerCase();
          // Only clear on definitive server-side invalidation - never on timeout/network
          const isTransient =
            isSupabaseTimeoutError(sessionError) ||
            msgLower.includes('timeout') ||
            msgLower.includes('network') ||
            msgLower.includes('fetch') ||
            msgLower.includes('cors') ||
            msgLower.includes('authretryablefetcherror') ||
            msgLower.includes('alpn') ||
            (sessionError as { name?: string })?.name === 'AuthRetryableFetchError';
          const isDefinitiveInvalid =
            msgLower.includes('refresh_token_not_found') ||
            msgLower.includes('refresh token not found') ||
            msgLower.includes('invalid refresh token') ||
            msgLower.includes('invalid_grant') ||
            msgLower.includes('refresh token has been revoked');
          if (!isTransient && isDefinitiveInvalid) {
            // Only hard-sign-out when storage recovery also fails.
            if (await tryRefreshPersistedSession()) {
              return;
            }
            if (mounted) {
              setAuthState({ user: null, isAuthenticated: false, isLoading: false });
              markInitialized();
            }
            clearAuthStorage();
            supabase.auth.signOut().catch(() => {});
            if (import.meta.env.DEV) {
              console.warn(
                'Auth session invalid (e.g. expired refresh token), cleared. Please sign in again.',
              );
            }
            return;
          } else if (!isTransient) {
            console.error('Session error:', sessionError);
          } else {
            // One clear message so users in India/weak networks know what happened
            console.warn(
              '[Auth] Session check failed (network/timeout or ALPN). Keeping persisted session if present.',
            );
          }
          // Never clear on transient errors — try refresh from persisted token.
          if (await tryRefreshPersistedSession()) {
            return;
          }
          if (mounted) {
            setAuthState((prev) => {
              // Keep optimistic persisted auth; only stop the spinner.
              if (prev.isAuthenticated && prev.user) {
                return { ...prev, isLoading: false };
              }
              return { ...prev, isLoading: false };
            });
            markInitialized();
          }
          return;
        }

        if (mounted && session?.user) {
          if (isPasswordRecoveryFlowActive()) {
            setAuthState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
            markInitialized();
            return;
          }

          clearPasswordRecovery();
          clearOAuthPending();
          bootstrapSessionUser(session.user, session.expires_at);
          if (hadOAuthCode) {
            stripOAuthParamsFromUrl();
          }
        } else if (mounted) {
          clearPasswordRecovery();
          // Google/SSO returned without a session — do not revive the previous
          // email/password session; that is exactly the "Google login failed" symptom.
          if (hadOAuthCode || isOAuthPending()) {
            clearOAuthPending();
            clearAuthStorage();
            markOAuthCallbackError('Google sign-in did not finish. Please try Continue with Google again.');
            stripOAuthParamsFromUrl();
            setAuthState({ user: null, isAuthenticated: false, isLoading: false });
            markInitialized();
            return;
          }
          if (await tryRefreshPersistedSession()) {
            return;
          }
          if (mounted) {
            setAuthState((prev) => {
              // No live session and no recoverable token — truly signed out.
              if (hasPersistedAuthSession(import.meta.env.VITE_SUPABASE_URL)) {
                return { ...prev, isLoading: false };
              }
              return { user: null, isAuthenticated: false, isLoading: false };
            });
          }
        }
        if (mounted) {
          markInitialized();
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        if (isSupabaseTimeoutError(error) || msg.toLowerCase().includes('timeout')) {
          console.warn('Session check timed out, attempting refresh from persisted storage.');
          if (await tryRefreshPersistedSession()) {
            return;
          }
        } else if (msg.toLowerCase().includes('refresh') && msg.toLowerCase().includes('token')) {
          if (await tryRefreshPersistedSession()) {
            return;
          }
          if (mounted) {
            setAuthState({ user: null, isAuthenticated: false, isLoading: false });
            markInitialized();
          }
          clearAuthStorage();
          supabase.auth.signOut().catch(() => {});
          if (import.meta.env.DEV) {
            console.warn('Auth refresh token invalid, cleared. Please sign in again.');
          }
          return;
        } else {
          console.error('Session check error:', error);
        }
        if (mounted) {
          setAuthState((prev) => {
            if (prev.isAuthenticated && prev.user) {
              return { ...prev, isLoading: false };
            }
            return { ...prev, isLoading: false };
          });
          markInitialized();
        }
      } finally {
        isCheckingSession = false;
      }
    };

    void checkSession();

    sessionCheckInterval = setInterval(
      () => {
        void checkSession();
      },
      5 * 60 * 1000,
    );

    // Defer async work to next tick so we don't hold auth-js internal lock (avoids deadlock with signIn)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecovery();
        if (mounted) {
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          markInitialized();
        }
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          if (currentPath !== '/reset-password') {
            const recoveryFragment = getRecoveryFragmentForRedirect();
            const target = recoveryFragment ? `/reset-password${recoveryFragment}` : '/reset-password';
            window.location.replace(target);
          }
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED' && !session) {
        setTimeout(async () => {
          try {
            if (await tryRefreshPersistedSession(1)) {
              return;
            }
          } catch {
            /* ignore */
          }
          // Keep last known auth state — do not sign out on a single refresh hiccup.
          if (mounted) {
            setAuthState((prev) => ({ ...prev, isLoading: false }));
          }
        }, 0);
        return;
      }

      if (event === 'SIGNED_IN' && session?.user) {
        setTimeout(() => {
          void (async () => {
            if (!mounted) return;
            clearPasswordRecovery();
            clearOAuthPending();

            const oauthAudit = consumeOAuthLoginAudit();

            try {
              await ensureTbSiteWorkspaceOrg();
            } catch {
              /* ignore */
            }

            await fetchUserProfile(session.user.id).catch(() => {});
            if (mounted) {
              setAuthState((prev) => (prev.isLoading ? { ...prev, isLoading: false } : prev));
            }

            if (oauthAudit?.provider) {
              let orgId = oauthAudit.organizationId;
              if (!orgId) {
                try {
                  const { data: profileRow } = await supabase
                    .from('profiles')
                    .select('organization_id')
                    .eq('id', session.user.id)
                    .maybeSingle();
                  orgId = profileAuditOrganizationId(profileRow);
                } catch {
                  /* ignore */
                }
              }

              recordAuthLoginOutcome({
                identifier: session.user.email || '',
                success: true,
                userId: session.user.id,
                organizationId: orgId,
                method: oauthAudit.provider === 'google' ? 'google' : 'enterprise_sso',
              });
            }
          })();
        }, 0);
        return;
      }

      // Ignore auth noise until the first session check finishes (use ref so this
      // effect is not torn down/recreated when isInitialized flips — that race was
      // revoking refresh tokens and bouncing users to the home page).
      if (!isInitializedRef.current) return;

      if (event === 'TOKEN_REFRESHED') {
        return;
      }

      if (event === 'SIGNED_OUT') {
        clearPasswordRecovery();
        // Transient refresh failures emit SIGNED_OUT even when a valid refresh token remains stored.
        if (hasPersistedAuthSession(import.meta.env.VITE_SUPABASE_URL) || loginInProgressRef.current) {
          setTimeout(() => {
            void (async () => {
              if (!mounted || loginInProgressRef.current) return;
              if (await tryRefreshPersistedSession(2)) {
                return;
              }
              const parsed = parsePersistedSupabaseSession(import.meta.env.VITE_SUPABASE_URL);
              if (
                parsed?.hasRefreshToken &&
                mounted &&
                staleSessionRecoveryRef.current < MAX_STALE_SESSION_RECOVERIES
              ) {
                staleSessionRecoveryRef.current += 1;
                bootstrapSessionUser({ id: parsed.userId, email: parsed.email }, null, {
                  deferReady: false,
                });
                return;
              }
              // Recovery exhausted — clear the dead token so the next load starts clean
              // instead of replaying SIGNED_OUT on every page view.
              clearAuthStorage();
              if (mounted) {
                setAuthState({
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                });
              }
            })();
          }, 0);
          return;
        }
        if (mounted) {
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
        return;
      }

      if (session?.user) {
        setTimeout(() => {
          if (!mounted) return;
          clearPasswordRecovery();
          fetchUserProfile(session.user.id).catch(() => {});
        }, 0);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval);
      }
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
    // Intentionally omit isInitialized — use isInitializedRef inside the listener.
    // Re-subscribing when it flips caused concurrent refreshSession races on every page load.
  }, [
    fetchUserProfile,
    clearPasswordRecovery,
    getRecoveryFragmentForRedirect,
    isPasswordRecoveryFlowActive,
    markPasswordRecovery,
    markInitialized,
  ]);

  const refreshUser = useCallback(async () => {
    // After signInWithPassword, getSession() can briefly return null on slow storage / mobile
    // (see login() Safari note). Retrying avoids falsely logging the user out in refreshUser.
    let userId: string | null = null;
    let sessionEmail = '';
    for (let attempt = 0; attempt < 6; attempt++) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user?.id) {
        userId = session.user.id;
        sessionEmail = session.user.email || '';
        break;
      }
      if (attempt < 5) {
        await new Promise((r) => setTimeout(r, 100 * (attempt + 1) + 50));
      }
    }

    if (!userId) {
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (!userErr && userData?.user?.id) {
        userId = userData.user.id;
        sessionEmail = userData.user.email || '';
      }
    }

    if (userId) {
      try {
        await fetchUserProfile(userId);
      } catch (error) {
        console.error('refreshUser: fetchUserProfile failed:', error);
        setAuthState((prev) => {
          if (!prev.isAuthenticated) {
            return {
              user: buildSessionBootstrapUser({
                id: userId!,
                email: sessionEmail,
              }),
              isAuthenticated: true,
              isLoading: false,
            };
          }
          return prev;
        });
      }
    } else {
      setAuthState((prev) => {
        // After password login, getSession() can lag briefly — do not wipe a valid
        // in-memory session or the workspace gate will bounce back to /login.
        if (prev.isAuthenticated && prev.user?.id) {
          return { ...prev, isLoading: false };
        }
        if (prev.isAuthenticated) {
          return {
            user: null,
            isAuthenticated: false,
            isLoading: false,
          };
        }
        return { ...prev, isLoading: false };
      });
    }
  }, [fetchUserProfile]);

  const applyRoleSwitch = useCallback(
    (newRole: string) => {
      if (isImpersonating && realUser) {
        const current = getImpersonationOverride(realUser.id);
        if (current?.target) {
          setImpersonationOverride(realUser.id, current.target, {
            workspaceOrgId: current.workspaceOrgId,
            actorHomeOrgId: current.actorHomeOrgId,
            actorIsPlatformAdmin: current.actorIsPlatformAdmin,
            actorIsOrgOwner: current.actorIsOrgOwner,
            viewAsRole: newRole,
          });
          setAuthState((prev) => {
            if (!prev.user) return prev;
            return { ...prev, user: { ...prev.user, role: newRole as User['role'] } };
          });
        }
        return;
      }

      const actorId = realUser?.id;
      const actorEmail = realUser?.email;
      const actorDbRole = realUser?.role;
      if (
        actorId &&
        isPlatformSuperAdminEmail(actorEmail) &&
        newRole === 'super_admin' &&
        resolvePlatformOwnerDashboardRole() !== 'super_admin'
      ) {
        restorePlatformOwnerSuperAdminDashboard(actorId);
      }

      setAuthState((prev) => {
        if (!prev.user) return prev;
        const email = (actorEmail || prev.user.email || '').trim();
        // Pass the real account role (not the preview target) so override rules resolve correctly.
        const baseRole = actorDbRole || prev.user.role || newRole;
        const resolvedRole = resolveEffectiveRole(actorId || prev.user.id, baseRole, email) as User['role'];
        // If override was just written for newRole but resolve still returns base, honor the switch.
        const nextRole =
          resolvedRole === newRole || getRoleViewOverride(actorId || prev.user.id)?.targetRole === newRole
            ? (newRole as User['role'])
            : resolvedRole;
        return {
          ...prev,
          user: { ...prev.user, role: nextRole },
        };
      });
    },
    [isImpersonating, realUser?.email, realUser?.id, realUser?.role],
  );

  const applyImpersonation = useCallback(
    async (
      target: ImpersonationTarget,
      options?: {
        workspaceOrgId?: string;
        actorIsPlatformAdmin?: boolean;
        actorIsOrgOwner?: boolean;
        viewAsRole?: string;
        rolePreview?: boolean;
      },
    ) => {
      if (!realUser) {
        throw new Error('Only super admins can impersonate users');
      }
      const { data: isPlatformAdmin } = await supabase.rpc('is_platform_admin');
      const actorIsPlatformAdmin = options?.actorIsPlatformAdmin ?? !!isPlatformAdmin;
      let actorIsOrgOwner = options?.actorIsOrgOwner === true;
      if (realUser.role !== 'super_admin' && !actorIsPlatformAdmin) {
        const orgId = options?.workspaceOrgId?.trim() || '';
        if (!orgId) {
          throw new Error('Only super admins can impersonate users');
        }
        const { data: team, error: teamError } = await supabase.rpc('saas_list_org_team_members', {
          p_org_id: orgId,
        });
        const rows = (team ?? []) as Array<{ user_id?: string; role?: string; status?: string }>;
        const actorOwnsOrg = rows.some(
          (row) => row.user_id === realUser.id && row.role === 'super_admin' && row.status === 'active',
        );
        const targetOnTeam = rows.some((row) => row.user_id === target.id && row.status === 'active');
        if (teamError || !actorOwnsOrg || !targetOnTeam) {
          throw new Error('Only super admins can view their own team');
        }
        actorIsOrgOwner = true;
      }
      if (!target.isActive) {
        throw new Error('Cannot impersonate inactive users');
      }

      clearRoleViewOverride(realUser.id);
      if (!options?.rolePreview) {
        clearRoleViewOverride(target.id);
      }

      const existing = getImpersonationOverride(realUser.id);
      const actorHomeOrgId = existing?.actorHomeOrgId?.trim() || (await fetchMyOrg())?.id || undefined;
      let workspaceOrgId = options?.workspaceOrgId?.trim() || '';
      if (!workspaceOrgId) {
        workspaceOrgId = (await fetchUserPrimaryOrgId(target.id)) ?? '';
      }

      setImpersonationOverride(realUser.id, target, {
        workspaceOrgId: workspaceOrgId || undefined,
        actorHomeOrgId,
        actorIsPlatformAdmin,
        actorIsOrgOwner,
        viewAsRole: options?.viewAsRole,
      });
      if (workspaceOrgId) {
        setActiveWorkspaceOrgId(workspaceOrgId);
        try {
          await setWorkspaceOrg(workspaceOrgId);
        } catch (err) {
          console.warn('applyImpersonation setWorkspaceOrg:', err);
        }
      }
      clearSaasMembershipCache();
      setIsImpersonating(true);
      const baseUser = impersonationTargetToUser(target);
      const effectiveRole = resolveEffectiveRole(target.id, baseUser.role) as User['role'];
      setAuthState((prev) => ({
        ...prev,
        user: { ...baseUser, role: effectiveRole },
      }));

      const { data: targetProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', target.id)
        .maybeSingle();
      const auditOrgId =
        typeof targetProfile?.organization_id === 'string' ? targetProfile.organization_id : undefined;

      await logSecurityEvent({
        userId: realUser.id,
        organizationId: auditOrgId,
        action: 'impersonation_start',
        resourceType: 'profile',
        resourceId: target.id,
        status: 'success',
        details: {
          targetRole: target.role,
          targetEmail: target.email,
          targetName: `${target.firstName} ${target.lastName}`.trim(),
        },
      });
    },
    [realUser],
  );

  const clearImpersonation = useCallback(async () => {
    if (!realUser) return;

    const override = getImpersonationOverride(realUser.id);
    clearImpersonationOverride(realUser.id);
    const { user: effectiveUser } = resolveEffectiveIdentity(realUser);
    setIsImpersonating(false);
    setAuthState((prev) => ({
      ...prev,
      user: effectiveUser,
    }));
    await restoreActorWorkspaceOrg(realUser.id, override?.actorHomeOrgId);
    clearSaasMembershipCache();

    if (override) {
      clearRoleViewOverride(override.target.id);
      const { data: targetProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', override.target.id)
        .maybeSingle();
      const auditOrgId =
        typeof targetProfile?.organization_id === 'string' ? targetProfile.organization_id : undefined;

      await logSecurityEvent({
        userId: realUser.id,
        organizationId: auditOrgId,
        action: 'impersonation_stop',
        resourceType: 'profile',
        resourceId: override.target.id,
        status: 'success',
      });
    }
  }, [realUser]);

  const login = async (emailOrPhone: string, password: string) => {
    loginInProgressRef.current = true;
    const loginIdentifier = (emailOrPhone || '').trim();
    let auditOrgId: string | undefined;
    try {
      if (!isSupabaseConfigured) {
        throw new Error(
          'Authentication is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your environment variables.',
        );
      }

      const trimmed = loginIdentifier;
      if (!trimmed) {
        throw new Error('Please enter your email or phone number.');
      }
      if (!password?.trim()) {
        throw new Error('Please enter your password.');
      }

      let loginEmail = trimmed;

      // Check if input is a phone number (contains only digits, +, spaces, or hyphens)
      const isPhone = /^[\d\s\+\-\(\)]+$/.test(trimmed);

      if (getAppMode() === 'saas-platform' && typeof window !== 'undefined') {
        const slug = new URLSearchParams(window.location.search).get('org')?.trim() || '';
        if (slug) {
          const { data: orgRow } = await supabase
            .from('organizations')
            .select('id')
            .eq('slug', slug)
            .maybeSingle();
          auditOrgId = typeof orgRow?.id === 'string' ? orgRow.id : undefined;
        }
      }

      if (isPhone) {
        if (getAppMode() === 'saas-platform' && !auditOrgId) {
          throw new Error(
            "Workspace organization is required for phone login. Open your organization's login page and try again.",
          );
        }

        // Look up the user's email via secure RPC function — does NOT expose
        // the full profiles table to anonymous users (replaced open USING(true) policy).
        const doPhoneLookup = async () => {
          let profileData: { email: string; is_active: boolean } | null = null;
          let retries = 0;
          const maxRetries = 2;

          while (retries <= maxRetries) {
            const { data, error } = await supabase.rpc('lookup_email_by_phone', {
              p_phone: emailOrPhone,
              ...(auditOrgId ? { p_organization_id: auditOrgId } : {}),
            });

            if (!error) {
              profileData = data && data.length > 0 ? data[0] : null;
              break;
            }

            if (error.message?.includes('timeout') || error.message?.includes('network')) {
              retries++;
              if (retries <= maxRetries) {
                await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
                continue;
              }
            }

            console.error('Phone lookup error:', error);
            throw new Error('Error looking up phone number. Please try again.');
          }

          if (!profileData) {
            throw new Error('No account found with this phone number');
          }
          if (profileData.is_active === false) {
            const support = getRuntimeAiSupportContact();
            const contactHint = [support.email, support.phone].filter(Boolean).join(' or ');
            throw new Error(
              contactHint
                ? `Your account has been deactivated. Please contact support at ${contactHint}`
                : 'Your account has been deactivated. Please contact support.',
            );
          }
          return profileData;
        };

        const profileData = await withTimeout(doPhoneLookup, 15000, 'Phone lookup timed out');
        loginEmail = profileData.email;
      } else {
        // Email login: no pre-login profile check. Go straight to Supabase Auth.
        // is_active is enforced after login when profile is fetched in background.
      }

      // A second organisation has its own auth user. Sign in with that login,
      // not whichever account was created first for the same email address.
      if (auditOrgId && getAppMode() === 'saas-platform') {
        const identity = await resolveCustomerLoginIdentity(
          isPhone ? '' : trimmed,
          isPhone ? trimmed : null,
          auditOrgId,
        );
        if (identity) {
          if (!identity.isActive) {
            const support = getRuntimeAiSupportContact();
            const contactHint = [support.email, support.phone].filter(Boolean).join(' or ');
            throw new Error(
              contactHint
                ? `Your account has been deactivated. Please contact support at ${contactHint}`
                : 'Your account has been deactivated. Please contact support.',
            );
          }
          loginEmail = identity.authEmail;
        }
      }

      // Supabase Auth stores emails in lowercase - normalize for reliable matching
      const emailForAuth = isPhone ? loginEmail : loginEmail.toLowerCase();

      const signIn = () =>
        supabase.auth.signInWithPassword({
          email: emailForAuth,
          password: password.trim(),
        });

      // 28s per attempt; more retries on mobile (flaky networks)
      const loginTimeoutMs = 28000;
      const isMobile =
        typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const maxAttempts = isMobile ? 3 : 2;
      let data: { user: { id: string; email?: string } | null } | null = null;
      let error: { message?: string } | null = null;

      console.log('[Auth] signInWithPassword start', {
        email: emailForAuth,
        isMobile,
        maxAttempts,
        loginTimeoutMsPerAttempt: loginTimeoutMs,
      });

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          console.log('[Auth] attempt', attempt, 'of', maxAttempts);
          const result = await withTimeout(signIn, loginTimeoutMs, 'Supabase login timed out');
          data = result.data;
          error = result.error;
          if (error) {
            console.warn('[Auth] attempt', attempt, 'error', {
              message: error.message,
              status: (error as { status?: number })?.status,
              code: (error as { code?: string })?.code,
            });
          } else {
            console.log('[Auth] attempt', attempt, 'success');
          }
        } catch (timeoutOrOther: unknown) {
          console.warn('[Auth] attempt', attempt, 'threw', timeoutOrOther);
          // Slow networks: sign-in may succeed even when the HTTP response times out
          if (isSupabaseTimeoutError(timeoutOrOther)) {
            try {
              const {
                data: { session },
              } = await supabase.auth.getSession();
              if (session?.user) {
                console.log('[Auth] session recovered after timeout');
                data = { user: session.user };
                error = null;
                break;
              }
            } catch {
              /* continue with retry / throw */
            }
          }
          if (isSupabaseTimeoutError(timeoutOrOther) && attempt < maxAttempts) {
            const delayMs = 1500 * attempt;
            console.log('[Auth] timeout; retrying in', delayMs, 'ms');
            await new Promise((r) => setTimeout(r, delayMs));
            continue;
          }
          throw timeoutOrOther;
        }
        if (!error) break;
        const isRetryable =
          (error?.message?.includes('fetch') ||
            error?.message?.includes('network') ||
            error?.message?.includes('timeout') ||
            error?.message?.includes('CORS')) &&
          attempt < maxAttempts;
        if (!isRetryable) break;
        const delayMs = 1000 * attempt;
        console.log('[Auth] retryable error; waiting', delayMs, 'ms before next attempt');
        await new Promise((r) => setTimeout(r, delayMs));
      }

      if (error) {
        const msg = error.message || String(error);
        const status = (error as { status?: number })?.status;
        const code = (error as { code?: string })?.code;
        console.error('[Auth] login failed', { error, message: msg, status, code });

        // Preserve raw error for display (add status/code so UI can show exact details)
        const enrichedError = Object.assign(new Error(msg), {
          status,
          code,
          cause: error,
        });

        // Provide user-friendly error messages but preserve raw for technical display
        if (msg.includes('Invalid login credentials') || msg.includes('Invalid password')) {
          throw Object.assign(
            new Error('Invalid email or password. Please check your credentials and try again.'),
            {
              status,
              code,
              cause: error,
            },
          );
        }
        if (msg.includes('rate limit') || msg.includes('max requests limit') || /upstash/i.test(msg)) {
          throw Object.assign(new Error('Too many login attempts. Please try again in a few minutes.'), {
            status,
            code,
            cause: error,
          });
        }
        if (
          msg.includes('Email not confirmed') ||
          msg.includes('email_not_confirmed') ||
          msg.includes('email confirmation')
        ) {
          throw Object.assign(
            new Error(
              "Email confirmation is required. The project needs 'Confirm email' DISABLED in Supabase Dashboard → Authentication → Providers → Email. " +
                "If you're the admin, see FIX_README.md for the fix. Otherwise, contact support.",
            ),
            { status, code, cause: error },
          );
        }
        if (msg.includes('fetch') || msg.includes('network') || msg.includes('CORS') || status === 0) {
          const freeTierMsg = getSupabaseFreeTierMessage(error);
          throw Object.assign(
            new Error(
              freeTierMsg ||
                'Unable to connect. Please check your internet connection and try again. If the problem persists, the service may be temporarily unavailable.',
            ),
            { status: status ?? 0, code, cause: error },
          );
        }

        const freeTierMsg = getSupabaseFreeTierMessage(error);
        if (freeTierMsg) {
          throw Object.assign(new Error(freeTierMsg), { status, code, cause: error });
        }
        throw enrichedError;
      }

      const authedUser = data?.user;
      if (authedUser) {
        console.log('[Auth] login success', { userId: authedUser.id, email: authedUser.email });
        // Clear magic link session to prevent conflicts - Supabase session is source of truth
        try {
          const { clearSession } = await import('../utils/sessionManager');
          clearSession();
        } catch {
          // Non-critical
        }
        // Set auth state immediately so login UI closes - don't block on profile fetch.
        // Profile fetch can hang on mobile (slow network, RLS timing). Fetch in background.
        const userEmail = authedUser.email || loginEmail;
        const isOwner = isPlatformSuperAdminEmail(userEmail);
        if (isOwner) {
          invalidateCachedValuesByPrefix('profile:');
        }
        const fallbackUser = buildSessionBootstrapUser({
          id: authedUser.id,
          email: userEmail,
        });
        // Authenticated immediately so the login modal can close; stay in loading until
        // workspace org is synced so the first dashboard queries are not empty under RLS.
        setAuthState({
          user: fallbackUser,
          isAuthenticated: true,
          isLoading: true,
        });
        markInitialized();

        try {
          const saasBootstrap =
            getAppMode() === 'saas-platform'
              ? (async () => {
                  await ensureImpersonationWorkspacePin(authedUser.id);
                  await Promise.all([ensureWorkspaceOrg(), ensureSaasOwnerProfile()]);
                })()
              : Promise.resolve();
          await Promise.all([ensureTbSiteWorkspaceOrg(), saasBootstrap]);
        } catch (workspaceErr) {
          console.warn('[Auth] workspace sync after password login failed:', workspaceErr);
        }

        try {
          await fetchUserProfile(authedUser.id);
        } catch (err) {
          console.warn('Background profile fetch failed (user already logged in):', err);
        } finally {
          setAuthState((prev) => ({ ...prev, isLoading: false }));
        }

        void resolveWorkspaceAuditOrgId().then((orgId) => {
          recordAuthLoginOutcome({
            identifier: loginIdentifier,
            success: true,
            userId: authedUser.id,
            organizationId: orgId,
            method: 'password',
          });
        });

        // iOS Safari: session can be missing from getSession() until app is backgrounded.
        // Trigger a delayed refresh so storage and auth state stay in sync on mobile.
        const isMobile =
          typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          setTimeout(() => {
            supabase.auth.refreshSession().catch(() => {});
          }, 200);
        }

        void verifyAuthSessionPersisted().then((persisted) => {
          if (!persisted && import.meta.env.DEV) {
            console.warn(
              '[Auth] Session was not written to browser storage — login may not survive a refresh. Check private browsing / storage settings.',
            );
          }
        });
      }
    } catch (error) {
      recordAuthLoginOutcome({
        identifier: loginIdentifier,
        success: false,
        reason: error instanceof Error ? error.message : String(error),
        organizationId: auditOrgId,
        method: 'password',
      });
      if (isSupabaseTimeoutError(error)) {
        console.error('[Auth] login timed out (all attempts)', error);
        const err = new Error('Login timed out. Please check your connection and try again.') as Error & {
          cause?: unknown;
        };
        err.cause = error;
        throw err;
      }
      console.error('[Auth] login failed (exception)', error);
      throw error;
    } finally {
      loginInProgressRef.current = false;
    }
  };

  const loginWithMagicLink = async (token: string) => {
    let pendingMagicLinkOrgId: string | undefined;
    try {
      const { getProfileById } = await import('../utils/profileManager');
      const { clearSession } = await import('../utils/sessionManager');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Authentication is not configured on this site.');
      }

      const sessionResponse = await fetch(`${supabaseUrl}/functions/v1/create-magic-link-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify({ magic_link_token: token }),
      });

      if (!sessionResponse.ok) {
        const errBody = (await sessionResponse.json().catch(() => ({}))) as { error?: string };
        throw new Error(mapMagicLinkSessionError(errBody.error));
      }

      const sessionData = await sessionResponse.json();
      if (!sessionData?.token || !sessionData?.profile_id) {
        throw new Error(mapMagicLinkSessionError('session_token_failed'));
      }

      pendingMagicLinkOrgId =
        typeof sessionData.organization_id === 'string' && sessionData.organization_id.length > 0
          ? sessionData.organization_id
          : undefined;

      // hashed_token from generateLink — same path as OTP/passkey. Do not read
      // profiles as anon first: RLS only allows a user to see their own row.
      let verifyData = await supabase.auth.verifyOtp({
        token_hash: sessionData.token,
        type: 'magiclink',
      });

      if (verifyData.error || !verifyData.data?.session) {
        const fallbackEmail =
          (typeof sessionData.email === 'string' && sessionData.email) ||
          resolveAuthSessionEmailFromProfile({
            email: sessionData.profile?.email,
            phone: sessionData.profile?.phone,
          });
        if (fallbackEmail) {
          verifyData = await supabase.auth.verifyOtp({
            token: sessionData.token,
            type: 'magiclink',
            email: fallbackEmail,
          });
        }
      }

      if (verifyData.error || !verifyData.data?.session) {
        throw new Error(
          verifyData.error?.message || 'Magic link session could not be verified. Please request a new link.',
        );
      }

      clearSession();

      const snapshot = readMagicLinkSessionProfile(sessionData);
      let profile: Profile | null = snapshot
        ? {
            id: snapshot.id,
            email: snapshot.email || sessionData.email || '',
            first_name: snapshot.first_name || '',
            last_name: snapshot.last_name || '',
            phone: snapshot.phone || '',
            role: snapshot.role || 'customer',
            has_real_email: false,
            is_active: snapshot.is_active !== false,
            created_at: snapshot.created_at || new Date().toISOString(),
            updated_at: snapshot.created_at || new Date().toISOString(),
            organization_id: snapshot.organization_id,
          }
        : null;

      const authedProfile =
        (await getProfileById(verifyData.data.session.user.id, {
          organizationId: pendingMagicLinkOrgId,
        })) ||
        (await getProfileById(sessionData.profile_id, {
          organizationId: pendingMagicLinkOrgId,
        }));
      if (authedProfile) {
        profile = authedProfile;
      }

      if (!profile) {
        throw new Error(mapMagicLinkSessionError('profile_not_found'));
      }

      if (sessionData.guest_scope_token) {
        const { storeGuestScopeToken } = await import('../utils/guestScopeEdge');
        storeGuestScopeToken(profile.id, sessionData.guest_scope_token);
      }

      const userEmail =
        resolveAuthSessionEmailFromProfile({
          email: profile.email,
          phone: profile.phone,
        }) || sessionData.email;

      setAuthState({
        user: {
          id: profile.id,
          email: profile.email,
          firstName: profile.first_name,
          lastName: profile.last_name,
          phone: profile.phone,
          role: (profile.role || 'customer') as UserRole,
          createdAt: profile.created_at,
          isActive: profile.is_active,
        },
        isAuthenticated: true,
        isLoading: false,
      });

      console.log('Magic link login successful');

      const magicLinkOrgId =
        typeof sessionData.organization_id === 'string' && sessionData.organization_id.length > 0
          ? sessionData.organization_id
          : profileAuditOrganizationId(profile);

      recordAuthLoginOutcome({
        identifier: profile.phone || userEmail,
        success: true,
        userId: profile.id,
        organizationId: magicLinkOrgId,
        method: 'magic_link',
      });

      const link = {
        purpose: sessionData.link_purpose ?? 'login',
        metadata: sessionData.link_metadata ?? {},
        profile_id: profile.id,
        phone: profile.phone,
      };

      return { link, profile };
    } catch (error) {
      console.error('Magic link login failed:', error);
      recordAuthLoginOutcome({
        identifier: '',
        success: false,
        reason: error instanceof Error ? error.message : String(error),
        organizationId: pendingMagicLinkOrgId,
        method: 'magic_link',
      });
      throw error;
    }
  };

  const logout = async () => {
    const signingOutUserId = realUser?.id;

    // Clear UI immediately — do not block on network during sign-out.
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
    setRealUser(null);
    setIsImpersonating(false);

    if (signingOutUserId) {
      clearImpersonationOverride(signingOutUserId);
      // Form drafts hold half-typed customer detail. On a shared machine they must not
      // outlive the session that created them.
      try {
        const { purgeDraftsForUser } = await import('../saas/lib/formDraft');
        purgeDraftsForUser(signingOutUserId);
      } catch {
        /* ignore */
      }
    }

    clearPasswordRecovery();
    try {
      const { clearSession } = await import('../utils/sessionManager');
      clearSession();
    } catch {
      /* ignore */
    }

    try {
      clearSupabaseAuthStorage(import.meta.env.VITE_SUPABASE_URL);
    } catch {
      /* ignore */
    }

    if (signingOutUserId) {
      void (async () => {
        let auditOrgId: string | undefined;
        try {
          if (getAppMode() === 'tb-site') {
            const { data: org } = await supabase
              .from('organizations')
              .select('id')
              .eq('slug', 'toolsandbubbles')
              .maybeSingle();
            auditOrgId = typeof org?.id === 'string' ? org.id : undefined;
          } else {
            const workspace = await ensureWorkspaceOrg();
            auditOrgId = workspace?.id;
          }
        } catch {
          /* ignore */
        }
        if (!auditOrgId) {
          const { data: actorProfile } = await supabase
            .from('profiles')
            .select('organization_id')
            .eq('id', signingOutUserId)
            .maybeSingle();
          auditOrgId =
            typeof actorProfile?.organization_id === 'string' ? actorProfile.organization_id : undefined;
        }
        await logSecurityEvent({
          userId: signingOutUserId,
          organizationId: auditOrgId,
          action: 'logout',
          status: 'success',
        });
      })().catch(() => {});
    }

    supabase.auth.signOut().catch(() => {});
    window.location.replace('/');
  };

  const register = async (userData: RegisterUserData) => {
    let authUserId: string | null = null;

    try {
      const trimmedEmail = userData.email?.trim();
      const rawPhone = userData.phone?.trim();
      const digitsOnly = rawPhone ? rawPhone.replace(/\D/g, '') : undefined;
      const normalizedPhone =
        rawPhone && rawPhone.startsWith('+')
          ? digitsOnly
            ? `+${digitsOnly}`
            : rawPhone
          : digitsOnly || rawPhone || null;
      const fallbackEmail =
        !trimmedEmail && normalizedPhone ? buildProfileGuestEmail(normalizedPhone) : undefined;
      const signupEmail = trimmedEmail || fallbackEmail;

      if (!signupEmail) {
        throw new Error('An email address or phone number is required to create an account.');
      }

      if (!userData.password || userData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long.');
      }

      const hasRealEmail =
        userData.hasRealEmail ?? Boolean(trimmedEmail && !isPlaceholderCustomerEmail(trimmedEmail));

      const targetOrgId = userData.organizationId?.trim() || '';
      const orgScopedCustomer = Boolean(targetOrgId) && (userData.role || 'customer') === 'customer';
      let authEmail = signupEmail;

      if (orgScopedCustomer) {
        const scope = await lookupCustomerAccountScope(signupEmail, normalizedPhone, targetOrgId);
        if (scope === 'this_org') {
          throw new Error(CUSTOMER_SAME_ORG_ACCOUNT_MESSAGE);
        }
        // Another organisation already has this person. Create a separate account
        // and password here. Their other login stays as it is.
        if (scope === 'other_org') {
          authEmail = orgScopedCustomerAuthEmail(targetOrgId, signupEmail);
        }
      } else {
        // Outside a tenant signup, one email or phone still means one account.
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id, email, phone')
          .or(buildEmailOptionalPhoneEqOrFilter(signupEmail, normalizedPhone))
          .maybeSingle();

        if (existingProfile) {
          if (existingProfile.email === signupEmail) {
            throw new Error('An account with this email already exists. Please sign in instead.');
          }
          if (normalizedPhone && existingProfile.phone === normalizedPhone) {
            throw new Error('An account with this phone number already exists. Please sign in instead.');
          }
        }
      }

      const signUpWith = (emailForAuth: string) =>
        supabase.auth.signUp({
          email: emailForAuth,
          password: userData.password,
          options: {
            emailRedirectTo: undefined,
            data: {
              first_name: userData.firstName,
              last_name: userData.lastName,
              phone: normalizedPhone ?? '',
              role: userData.role || 'customer',
              contact_email: signupEmail,
            },
          },
        });

      let { data, error } = await signUpWith(authEmail);

      const alreadyRegistered = (message?: string) =>
        Boolean(message?.includes('already registered') || message?.includes('already been registered'));

      // The real address is already an auth login somewhere else. This organisation
      // still gets its own account when the earlier lookup did not see that profile.
      if (error && alreadyRegistered(error.message) && orgScopedCustomer && authEmail === signupEmail) {
        authEmail = orgScopedCustomerAuthEmail(targetOrgId, signupEmail);
        ({ data, error } = await signUpWith(authEmail));
      }

      // If signup fails, throw the error
      if (error) {
        // Handle specific error cases
        if (alreadyRegistered(error.message)) {
          throw new Error(
            orgScopedCustomer
              ? CUSTOMER_SAME_ORG_ACCOUNT_MESSAGE
              : 'An account with this email already exists. Please sign in instead.',
          );
        }
        if (error.message?.includes('rate limit')) {
          throw new Error('Too many signup attempts. Please try again in a few minutes.');
        }
        throw error;
      }

      // Track auth user ID for cleanup if profile creation fails
      authUserId = data.user?.id || null;

      // Some signups confirm the email without returning a session. The profile
      // insert is only allowed for the signed-in person, so sign in with the
      // password they just chose before saving their organisation record.
      if (data.user && !data.session) {
        const signedIn = await supabase.auth.signInWithPassword({
          email: authEmail,
          password: userData.password,
        });
        if (signedIn.error || !signedIn.data.session) {
          throw new Error(
            orgScopedCustomer
              ? 'Your account was created. Sign in with the password you just chose.'
              : signedIn.error?.message || 'Account created. Please sign in.',
          );
        }
      }

      // If user was created successfully, create profile
      if (data.user) {
        let signupOrgId: string | undefined = userData.organizationId?.trim() || undefined;
        let signupOrgSlug: string | undefined;
        if (!signupOrgId && getAppMode() === 'saas-platform') {
          signupOrgSlug =
            localStorage.getItem(SAAS_ORG_SLUG_KEY) ||
            new URLSearchParams(window.location.search).get('org') ||
            undefined;
          if (signupOrgSlug?.trim()) {
            const { data: orgRow } = await supabase
              .from('organizations')
              .select('id')
              .eq('slug', signupOrgSlug.trim())
              .maybeSingle();
            signupOrgId = orgRow?.id;
          }
        }

        // Check for referral code in localStorage
        const referralCode = localStorage.getItem('referral_code');
        let referrerId = null;

        if (referralCode) {
          try {
            const { data: referrerIdFromRpc, error: referralRpcError } = await supabase.rpc(
              'find_referrer_id_by_code',
              {
                p_referral_code: referralCode,
                p_organization_id: signupOrgId ?? null,
              },
            );

            if (!referralRpcError && referrerIdFromRpc) {
              referrerId = referrerIdFromRpc as string;
              console.log('Referral code matched:', referralCode, '-> User:', referrerId);
            }
          } catch (referralError) {
            console.warn('Error checking referral code:', referralError);
            // Continue without referral code if lookup fails
          }
        }

        // Retry profile creation up to 3 times for transient errors
        let profileError = null;
        let retries = 0;
        const maxRetries = 3;

        while (retries < maxRetries) {
          const { error: insertError } = await supabase.from('profiles').insert({
            id: data.user.id,
            email: signupEmail,
            first_name: userData.firstName,
            last_name: userData.lastName,
            phone: normalizedPhone ?? null,
            role: userData.role || 'customer',
            has_real_email: hasRealEmail,
            referred_by: referrerId,
            address: userData.address ?? null,
            city: userData.city ?? null,
            ...(signupOrgId ? { organization_id: signupOrgId } : {}),
          });

          if (!insertError) {
            profileError = null;
            break;
          }

          // A customer-number clash is not a duplicate person. Ask for another number.
          if (insertError.code === '23505' && insertError.message?.includes('customer_number')) {
            retries++;
            if (retries < maxRetries) continue;
            profileError = insertError;
            break;
          }

          // If duplicate key error, user already exists
          if (insertError.code === '23505') {
            profileError = insertError;
            break;
          }

          // If it's a transient error, retry
          if (
            insertError.code === 'PGRST116' ||
            insertError.message?.includes('timeout') ||
            insertError.message?.includes('network')
          ) {
            retries++;
            if (retries < maxRetries) {
              await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
              continue;
            }
          }

          profileError = insertError;
          break;
        }

        if (profileError) {
          // If profile insert fails due to duplicate, it means user already exists
          if (profileError.code === '23505') {
            // Check if profile actually exists (race condition - another signup completed)
            const { data: existingProfile } = await supabase
              .from('profiles')
              .select('id')
              .eq('id', data.user.id)
              .maybeSingle();

            if (existingProfile) {
              // Profile exists, signup was successful (race condition)
              console.log('Profile already exists, signup successful');
              return;
            }

            throw new Error(
              orgScopedCustomer
                ? CUSTOMER_SAME_ORG_ACCOUNT_MESSAGE
                : 'An account with this email or phone number already exists. Please sign in instead.',
            );
          }

          // For other errors, provide helpful message
          console.error('Profile creation error:', profileError);
          throw new Error(
            profileError.message ||
              'Failed to create profile. Please try again or contact support if the problem persists.',
          );
        }

        if (orgScopedCustomer) {
          await attachSignedInCustomerToOrganization(targetOrgId);
        }

        // Clear referral code after successful registration
        if (referralCode) {
          localStorage.removeItem('referral_code');
        }

        // Send welcome email (non-blocking)
        if (hasRealEmail) {
          const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(' ').trim();

          // Don't wait for email to send - it's not critical
          let welcomeOrgId: string | undefined = signupOrgId;
          let welcomeOrgSlug: string | undefined = signupOrgSlug;
          if (!welcomeOrgSlug && getAppMode() === 'saas-platform') {
            welcomeOrgSlug =
              localStorage.getItem(SAAS_ORG_SLUG_KEY) ||
              new URLSearchParams(window.location.search).get('org') ||
              undefined;
            if (welcomeOrgSlug?.trim()) {
              const { data: orgRow } = await supabase
                .from('organizations')
                .select('id')
                .eq('slug', welcomeOrgSlug.trim())
                .maybeSingle();
              welcomeOrgId = orgRow?.id;
            }
          }

          supabase.functions
            .invoke('send-welcome-email', {
              body: {
                customerEmail: signupEmail,
                customerName: fullName || userData.firstName || signupEmail,
                customerPhone: normalizedPhone ?? undefined,
                organizationId: welcomeOrgId,
                organizationSlug: welcomeOrgSlug?.trim(),
              },
            })
            .catch((welcomeError) => {
              console.error('Failed to send welcome email:', welcomeError);
              // Don't throw - email failure shouldn't block registration
            });
        }
      }
    } catch (err: any) {
      // If error is about email confirmation/sending, but user was created, check if profile exists
      if (
        err.message &&
        (err.message.includes('confirmation') ||
          err.message.includes('email') ||
          err.message.includes('smtp') ||
          err.message.includes('send'))
      ) {
        // Check if profile was actually created
        if (authUserId) {
          const { data: profileCheck } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', authUserId)
            .maybeSingle();

          if (profileCheck) {
            // Profile exists, signup was successful despite email error
            console.warn('User created successfully, but email notification failed');
            return;
          }
        }
      }

      // Re-throw errors with better messages
      if (err.message?.includes('already exists') || err.message?.includes('already registered')) {
        throw err;
      }

      // Provide user-friendly error messages
      const errorMessage = err.message || 'Failed to create account. Please try again.';
      throw new Error(errorMessage);
    }
  };

  const loginWithOTP = async (profile: any, authToken?: string) => {
    try {
      if (!profile || typeof profile !== 'object') {
        throw new Error('Invalid profile data received');
      }

      const { data: existingSession } = await supabase.auth.getSession();
      if (existingSession?.session?.user?.id) {
        const sessionProfile = await fetchUserProfile(existingSession.session.user.id);
        setAuthState({
          user: sessionProfile,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      }

      if (!authToken) {
        throw new Error('Secure session token missing. Please try again.');
      }

      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: authToken,
        type: 'magiclink',
      });

      if (error || !data?.session) {
        throw new Error(error?.message || 'Could not establish secure session. Please try again.');
      }

      console.log('[Auth] OTP Supabase session created for', data.session.user.id);
      const sessionProfile = await fetchUserProfile(data.session.user.id);
      setAuthState({
        user: sessionProfile,
        isAuthenticated: true,
        isLoading: false,
      });

      void verifyAuthSessionPersisted().then((persisted) => {
        if (!persisted && import.meta.env.DEV) {
          console.warn(
            '[Auth] OTP session was not written to durable storage — login may not survive closing the app.',
          );
        }
      });

      const isMobile =
        typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        setTimeout(() => {
          supabase.auth.refreshSession().catch(() => {});
        }, 200);
      }

      let otpOrgId = profileAuditOrganizationId(profile as { organization_id?: string | null });
      if (!otpOrgId) {
        try {
          const workspace = await ensureWorkspaceOrg();
          otpOrgId = workspace?.id;
        } catch {
          /* ignore */
        }
      }

      recordAuthLoginOutcome({
        identifier: profile.phone || profile.email || sessionProfile?.phone || '',
        success: true,
        userId: sessionProfile?.id || profile.id,
        organizationId: otpOrgId,
        method: 'otp',
      });
    } catch (error) {
      console.error('[Auth] OTP login error:', error);
      let otpOrgId = profileAuditOrganizationId(profile as { organization_id?: string | null });
      if (!otpOrgId) {
        otpOrgId = await resolveWorkspaceAuditOrgId();
      }
      recordAuthLoginOutcome({
        identifier: profile?.phone || profile?.email || '',
        success: false,
        userId: profile?.id,
        reason: error instanceof Error ? error.message : String(error),
        organizationId: otpOrgId,
        method: 'otp',
      });
      throw error;
    }
  };

  const loginWithPasskey = async (cachedOptionsJSON?: Record<string, unknown>) => {
    let passkeyEmail = '';
    try {
      const result = await authenticatePasskey(cachedOptionsJSON);
      if (result.ok === false) {
        throw new Error(result.error || 'Passkey sign-in failed');
      }
      passkeyEmail = result.email;
      console.log('[Auth] passkey auth ok, verifying session for', result.email);
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: result.token,
        type: 'magiclink',
      });
      if (error) {
        console.error('[Auth] passkey verifyOtp error:', error);
        throw new Error(`Sign-in failed: ${error.message}`);
      }
      if (!data?.session) {
        console.error('[Auth] passkey verifyOtp no session', data);
        throw new Error('No session returned. Please try again.');
      }
      console.log('[Auth] passkey session created for', data.session.user.id);
      const profile = await fetchUserProfile(data.session.user.id);
      setAuthState({
        user: profile,
        isAuthenticated: true,
        isLoading: false,
      });

      if (profile) {
        recordAuthLoginOutcome({
          identifier: profile.phone || profile.email || '',
          success: true,
          userId: profile.id,
          organizationId: profileAuditOrganizationId(profile as { organization_id?: string | null }),
          method: 'passkey',
        });
      }
    } catch (error) {
      recordAuthLoginOutcome({
        identifier: passkeyEmail,
        success: false,
        reason: error instanceof Error ? error.message : String(error),
        organizationId: await resolveWorkspaceAuditOrgId(),
        method: 'passkey',
      });
      throw error;
    }
  };

  const loginWithGoogle = async (): Promise<'redirect' | 'session'> => {
    if (!isSupabaseConfigured) {
      throw new Error(
        'Authentication is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your environment variables.',
      );
    }
    if (typeof window === 'undefined') {
      throw new Error('Google sign-in is only available in the browser.');
    }
    if (ensureTbCanonicalHost()) {
      return 'redirect';
    }

    // Clear any existing email/password session first. Otherwise OAuth return can leave the
    // previous JWT in storage and refresh restores that user instead of the Google account.
    loginInProgressRef.current = true;
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      clearSupabaseAuthStorage(import.meta.env.VITE_SUPABASE_URL);
    }
    setAuthState({ user: null, isAuthenticated: false, isLoading: true });
    setRealUser(null);
    markOAuthPending('google');

    stashSaasOAuthReturn();
    const redirectTo = buildOAuthRedirectUrl();
    stashOAuthLoginAudit({ provider: 'google' });

    if (shouldUseServiceAppGoogleAuth()) {
      const saasClientId = await fetchSaasGoogleOAuthClientId();
      if (!saasClientId) {
        clearOAuthPending();
        loginInProgressRef.current = false;
        throw new Error(
          'ServiceApp Google sign-in is not configured yet. In Platform Admin → Auth, set the ServiceApp Google OAuth Client ID (and SAAS_GOOGLE_CLIENT_SECRET in Vercel). Do not use the Tools & Bubbles Google app.',
        );
      }
      try {
        await signInWithServiceAppGoogle(saasClientId);
        clearOAuthPending();
        loginInProgressRef.current = false;
        return 'session';
      } catch (error) {
        clearOAuthPending();
        loginInProgressRef.current = false;
        void resolveWorkspaceAuditOrgId().then((orgId) => {
          recordAuthLoginOutcome({
            identifier: '',
            success: false,
            reason: error instanceof Error ? error.message : 'Google sign-in failed',
            organizationId: orgId,
            method: 'google',
          });
        });
        throw error instanceof Error ? error : new Error('Google sign-in failed');
      }
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        scopes: 'email profile openid',
        queryParams: {
          prompt: 'select_account',
        },
      },
    });
    if (error) {
      clearOAuthPending();
      loginInProgressRef.current = false;
      void resolveWorkspaceAuditOrgId().then((orgId) => {
        recordAuthLoginOutcome({
          identifier: '',
          success: false,
          reason: error.message || 'Google sign-in failed',
          organizationId: orgId,
          method: 'google',
        });
      });
      throw new Error(error.message || 'Google sign-in failed');
    }
    return 'redirect';
  };

  const loginWithEnterpriseSSO = async (params: { domain?: string; providerId?: string }) => {
    if (!isSupabaseConfigured) {
      throw new Error(
        'Authentication is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your environment variables.',
      );
    }
    if (typeof window === 'undefined') {
      throw new Error('Enterprise SSO is only available in the browser.');
    }
    if (ensureTbCanonicalHost()) {
      return;
    }
    const domain = params.domain?.trim().toLowerCase();
    const providerId = params.providerId?.trim();
    if (!domain && !providerId) {
      throw new Error('SSO requires an organization email domain or provider id.');
    }
    loginInProgressRef.current = true;
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      clearSupabaseAuthStorage(import.meta.env.VITE_SUPABASE_URL);
    }
    setAuthState({ user: null, isAuthenticated: false, isLoading: true });
    setRealUser(null);
    markOAuthPending('enterprise_sso');
    stashSaasOAuthReturn();
    const redirectTo = buildOAuthRedirectUrl();
    const auditOrgId = await resolveWorkspaceAuditOrgId();
    stashOAuthLoginAudit({
      provider: providerId || domain || 'enterprise_sso',
      organizationId: auditOrgId,
    });
    const { data, error } = await supabase.auth.signInWithSSO(
      providerId ? { providerId, options: { redirectTo } } : { domain: domain!, options: { redirectTo } },
    );
    if (error) {
      clearOAuthPending();
      loginInProgressRef.current = false;
      recordAuthLoginOutcome({
        identifier: domain || providerId || '',
        success: false,
        reason: error.message || 'Enterprise SSO sign-in failed',
        organizationId: auditOrgId,
        method: 'enterprise_sso',
      });
      throw new Error(error.message || 'Enterprise SSO sign-in failed');
    }
    if (data?.url) {
      window.location.href = data.url;
      return;
    }
    clearOAuthPending();
    loginInProgressRef.current = false;
    throw new Error('No SSO redirect URL returned. Check your IdP configuration in Supabase.');
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        loginWithMagicLink,
        loginWithOTP,
        loginWithPasskey,
        loginWithGoogle,
        loginWithEnterpriseSSO,
        logout,
        register,
        mustChangePassword,
        refreshUser,
        realUser,
        isImpersonating,
        applyImpersonation,
        clearImpersonation,
        applyRoleSwitch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
